import { useState } from "react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { ListToolbar } from "@/components/shared/ListToolbar";
import { ListPagination } from "@/components/shared/ListPagination";
import {
  DataTable,
  type DataTableColumn,
  type SelectionMode,
} from "@/components/shared/DataTable";
import { SelectionModeControl } from "@/components/shared/SelectionModeControl";
import { useUserList, type User } from "@/hooks/useUsers";
import { formatDate } from "@/utils/format";
import { downloadCsv } from "@/utils/exportCsv";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_PAGE_SIZE = 25;

const exportRows = (rows: User[]) =>
  downloadCsv(
    "customers",
    ["Phone", "Email", "Care type", "City", "Registered"],
    rows.map((u) => [
      u.phone,
      u.email ?? "",
      u._count ? `${u._count.bookings} bookings` : "",
      "",
      formatDate(u.createdAt),
    ]),
  );

export const CustomersList = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [selection, setSelection] = useState<SelectionMode>("none");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { can } = useAuth();

  const { data, isLoading, error, refetch } = useUserList({
    role: "CUSTOMER",
    q: search || undefined,
    page,
    limit,
  });
  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const setSelectionMode = (m: SelectionMode) => {
    setSelection(m);
    setSelectedIds([]);
  };

  const columns: Array<DataTableColumn<User>> = [
    {
      key: "phone",
      header: "Phone",
      render: (u) => <span className="font-medium">{u.phone}</span>,
      sortValue: (u) => u.phone,
    },
    {
      key: "email",
      header: "Email",
      render: (u) => (
        <span className="text-muted-foreground">{u.email ?? "—"}</span>
      ),
      sortValue: (u) => u.email ?? "",
    },
    {
      key: "care",
      header: "Care type",
      render: (u) => (
        <span className="text-muted-foreground">
          {u._count ? `${u._count.bookings} bookings` : "—"}
        </span>
      ),
      sortValue: (u) => u._count?.bookings ?? 0,
    },
    {
      key: "city",
      header: "City",
      render: () => <span className="text-muted-foreground">—</span>,
    },
    {
      key: "registered",
      header: "Registered",
      render: (u) => (
        <span className="text-muted-foreground">{formatDate(u.createdAt)}</span>
      ),
      sortValue: (u) => new Date(u.createdAt),
    },
  ];

  return (
    <AppLayout title="Customers">
      <ListToolbar
        search={{
          value: search,
          onChange: (v) => {
            setSearch(v);
            setPage(1);
          },
          placeholder: "Search by phone or email...",
        }}
        onExport={
          can("customers", "export") ? () => exportRows(rows) : undefined
        }
        actions={
          <SelectionModeControl value={selection} onChange={setSelectionMode} />
        }
      />

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-btn px-4 py-2 mb-3 text-sm">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                toast.success(
                  `Message sent to ${selectedIds.length} customers`,
                );
                setSelectedIds([]);
              }}
              className="px-3 py-1 rounded-btn border border-border hover:bg-muted"
            >
              Send message
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3 py-1 rounded-btn text-muted-foreground hover:bg-muted"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
        emptyTitle="No customers yet"
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(u) => u.id}
          selection={selection}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="customers"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>
    </AppLayout>
  );
};

export default CustomersList;
